import React, { useEffect, useState } from 'react';
import { FileBrowser } from './FileBrowser';
import { useClientStorageListing } from './useClientStorageListing';

interface Props {
  projectId: string;
  selectedPath: string | null;
  onPathChange: (path: string) => void;
  disabled: boolean;
  /** When true, only directories are listed/selectable (used by the export destination picker). */
  foldersOnly?: boolean;
  /** Receives the listing's bucket name; submitted as `clientStorageBucketName`. */
  onBucketChange?: (bucket: string) => void;
  /** Receives the folder currently open (after navigation / level-up). */
  onBaseDirChange?: (baseDir: string) => void;
  title?: string;
  showEmptyAlert?: boolean;
  onMatterIdChange: (matterId: string | undefined) => void;
}

export function ClientStorageBrowser({
  projectId,
  selectedPath,
  onPathChange,
  disabled,
  foldersOnly = false,
  onBucketChange,
  onBaseDirChange,
  title = 'Client Storage Listing',
  onMatterIdChange,
  showEmptyAlert = true
}: Props) {
  const [baseDir, setBaseDir] = useState('');
  // baseDir is part of the query key, so changing it refetches automatically.
  const { data, isFetching, refetch } = useClientStorageListing(projectId, baseDir, true);

  // Surface the bucket name / open folder to the parent. Both effects key only on the
  // value, not the callback prop — an inline-arrow callback changes every render and
  // would loop with the parent's setState.
  useEffect(() => {
    if (data?.bucketName) onBucketChange?.(data.bucketName);
  }, [data?.bucketName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onBaseDirChange?.(baseDir);
  }, [baseDir]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onMatterIdChange?.(data?.matterId);
  }, [data?.matterId]); // eslint-disable-line react-hooks/exhaustive-deps

  const pathLabel = baseDir.startsWith('/') ? baseDir : `/${baseDir}`;
  const files = (data?.files ?? []).filter(f => !foldersOnly || f.isDirectory);

  const levelUp = () => {
    const parts = baseDir.split('/').filter(Boolean);
    parts.pop();
    setBaseDir(parts.join('/'));
  };

  return (
    <div>
      <h4>
        <button
          type="button"
          className="btn btn-default btn-xs"
          onClick={() => refetch()}
          disabled={isFetching || disabled}
        >
          <i className={`fas ${isFetching ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} aria-hidden="true" />
        </button>{' '}
        {title}
      </h4>
      <FileBrowser
        pathLabel={pathLabel}
        files={files}
        loading={isFetching}
        selectedPath={selectedPath}
        onSelect={onPathChange}
        onNavigate={setBaseDir}
        onLevelUp={levelUp}
        showEmptyAlert={showEmptyAlert}
      />
    </div>
  );
}
